import { lighten, Stack, useTheme } from "@mui/material";
import { SSButton } from "src/components/base/components/SSButton";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { TEvent } from "src/shared/types/globalStotre.types";
import { formatSqlDate } from "src/utils/utils";
import { FiEdit } from "react-icons/fi";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useAttendanceModal } from "./EventAttendanceModal";
import React from "react";
import {
  isEventMaxSeatLimitReached,
  isReservationLimitReached,
  hasSecondaryConfirmation,
  getSecondaryConfirmationStatus,
  shouldShowExchangeButton,
} from "./utils.event-attendance";
import { useEventAttendance } from "./useEventAttendance";
import { useExchangeModal } from "./useExchangeReservation";

interface EventAttendaceWidgetProps {
  event: TEvent;
  isDarkBg?: boolean;
}

export function EventAttendaceWidget(props: EventAttendaceWidgetProps) {
  const teamConfig = useTeamConfig();
  const { HOME_PAGE_SETTINGS, TEAM_GLOBAL_SETTINGS } = teamConfig;
  const theme = useSSTheme();
  const muiTheme = useTheme();
  const { md: mdUp } = useCssMediaQuery("up");
  const successMainLightColor = lighten(muiTheme.palette.success.main, 0.2);
  const errorMainLightColor = lighten(muiTheme.palette.error.main, 0.2);
  const warningMainColor = muiTheme.palette.warning.main;
  const warningDarkColor = HOME_PAGE_SETTINGS?.EVENT_CARD?.CONFIRM_ATTENDANCE_BUTTON_BACKGROUND_COLOR || muiTheme.palette.warning.dark; // prettier-ignore
  const attendanceModal = useAttendanceModal();
  const { passesOnAccount, events, optedInCountForEventReservation } = useGlobalStore();
  const event = props?.event;
  const passSeatCount = event?.seatInformation?.PASS || 0;
  const { isReservationEnabled } = useEventAttendance();

  const exchangeModal = useExchangeModal();
  const openExchangeModal = (event: TEvent) => exchangeModal.set({ isOpen: true, newEvent: event });
  const attendButtonColor = HOME_PAGE_SETTINGS?.EVENT_CARD?.ATTEND_BUTTON_COLOR || TEAM_GLOBAL_SETTINGS?.COLORS_PRIMARY as string; // prettier-ignore
  const attendButtonLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.ATTEND_BUTTON_LABEL || "Attend"; // prettier-ignore
  const attendButtonAttendingLabel = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.ATTEND_BUTTON_ATTENDING_LABEL || "Attending"; // prettier-ignore
  const attendButtonNotAttendingLabel = HOME_PAGE_SETTINGS?.ATTEND_EVENT_MODAL?.ATTEND_BUTTON_NOT_ATTENDING_LABEL || "Not Attending"; // prettier-ignore
  const hideTicketDeliveryStatus = HOME_PAGE_SETTINGS?.EVENT_CARD?.HIDE_TICKET_DELIVERY_STATUS ?? true; // prettier-ignore
  const checkInLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.CHECK_IN_LABEL || "Check In"; // prettier-ignore
  const reservationCoverageExceededMessage =
    HOME_PAGE_SETTINGS?.EVENT_CARD?.RESERVATION_COVERAGE_EXCEEDED_MESSAGE;
  const ticketNotDelivered = Boolean(props?.event?.ticketDeliveryStatus?.status?.includes("No")); // prettier-ignore

  // The delivered-state copy comes from the backend and is shared by every team
  // schema, so teams that need their own wording (e.g. lottery language) can
  // override just the delivered headline / sub-line from their config. The
  // "no ticket delivered" state always keeps the backend copy.
  const ticketDeliveredLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.TICKET_DELIVERED_LABEL; // prettier-ignore
  const ticketDeliveredMessage = HOME_PAGE_SETTINGS?.EVENT_CARD?.TICKET_DELIVERED_MESSAGE; // prettier-ignore
  const deliveryStatusLabel = !ticketNotDelivered && ticketDeliveredLabel ? ticketDeliveredLabel : props?.event?.ticketDeliveryStatus?.status; // prettier-ignore
  const deliveryStatusMessage = !ticketNotDelivered && ticketDeliveredMessage ? ticketDeliveredMessage : props?.event?.ticketDeliveryStatus?.message; // prettier-ignore
  const attendButtonTextColor = HOME_PAGE_SETTINGS?.EVENT_CARD?.ATTEND_BUTTON_TEXT_COLOR || theme.colors.common.white; // prettier-ignore

  // secondary confirmation labels
  const confirmAttendanceLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.CONFIRM_ATTENDANCE_BUTTON_LABEL || "Confirm Attendance"; // prettier-ignore
  const confirmedLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.CONFIRMED_LABEL || "Confirmed"; // prettier-ignore
  const confirmationMissedLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.CONFIRMATION_MISSED_LABEL || "Confirmation Missed"; // prettier-ignore
  const confirmationOpensLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.CONFIRMATION_OPENS_LABEL || "Confirmation opens"; // prettier-ignore
  const confirmByLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.CONFIRM_BY_LABEL || "Confirm by"; // prettier-ignore
  const confirmationWindowClosedLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.CONFIRMATION_WINDOW_CLOSED_LABEL || "Confirmation window has closed"; // prettier-ignore

  const exchangeButtonLabel = HOME_PAGE_SETTINGS?.EVENT_CARD?.EXCHANGE_BUTTON_LABEL || "Reserve with Exchange"; // prettier-ignore

  // checking if the event max seat limit has been reached
  const isEventSeatLimitReached = isEventMaxSeatLimitReached(props.event);

  // When set, "reservations used" comes from the backend count (which for
  // fixed-budget teams includes past events) instead of the local upcoming
  // events array — past events silently drop out of that array, which would
  // otherwise make a used slot look available again.
  const useOptedInCountForEventReservation = HOME_PAGE_SETTINGS?.EVENT_CARD?.USE_OPTED_IN_COUNT_FOR_EVENT_RESERVATION ?? false; // prettier-ignore

  // checking if the max reservation limit has been reached
  const isMaxReservationReached = isReservationLimitReached({
    passesOnAccount,
    events,
    event,
    optedInCountForEventReservation,
    useOptedInCountForEventReservation,
  });

  // creating the disabled boolean for the attendance button
  const isAttendenceDisabled = isEventSeatLimitReached || isMaxReservationReached; // prettier-ignore

  // is the reserve-with-exchange flow enabled for this team (opt-in)
  const isExchangeEnabled = HOME_PAGE_SETTINGS?.EVENT_CARD?.IS_RESERVATION_EXCHANGE_ENABLED ?? false;

  const isExchange = shouldShowExchangeButton({
    event,
    passesOnAccount,
    events,
    isReservationEnabled,
    isExchangeEnabled,
    optedInCountForEventReservation,
    useOptedInCountForEventReservation,
  });

  const handleOpenAttendanceModal = () => {
    attendanceModal.set({ isOpen: true, event: props.event });
  };

  // if the event has no opt in start date time, return null
  if (!props?.event?.optInStartDateTime) return null;

  // if the event reservation enabled and the pass seat count is 0, return null
  if (isReservationEnabled && passSeatCount <= 0) return null;

  // --- Secondary Confirmation States ---
  if (hasSecondaryConfirmation(props.event)) {
    const secondaryStatus = getSecondaryConfirmationStatus(props.event);

    // Awaiting secondary window: show "Attending" + caption with opening date
    if (secondaryStatus === "awaiting_window") {
      return (
        <Stack spacing={0.5} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
          <SSButton
            variant="outlined"
            colorScheme={successMainLightColor}
            endIcon={<FiEdit />}
            sx={{
              color: successMainLightColor,
              fontWeight: 700,
              fontSize: "0.9rem",
              ...(mdUp ? { width: "fit-content" } : {}),
            }}
            onClick={handleOpenAttendanceModal}
            fullWidth={!mdUp}
          >
            {attendButtonAttendingLabel}
          </SSButton>
          <SSText
            variant="caption"
            color={warningMainColor}
            fontSize="0.8rem"
            fontWeight={500}
          >
            {confirmationOpensLabel}{" "}
            {formatSqlDate(props.event.secondaryStartDateTime!, "MMM dd, h:mm a")}
          </SSText>
        </Stack>
      );
    }

    // Secondary window open: show prominent "Confirm Attendance" button
    if (secondaryStatus === "window_open") {
      return (
        <Stack spacing={0.5} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
          <SSButton
            onClick={handleOpenAttendanceModal}
            sx={{
              backgroundColor: warningDarkColor,
              color: theme.colors.common.white,
              fontWeight: 700,
              "&:hover": {
                backgroundColor: lighten(warningDarkColor, 0.15),
              },
              ...(mdUp ? { width: "fit-content" } : {}),
            }}
            fullWidth={!mdUp}
          >
            {confirmAttendanceLabel}
          </SSButton>
          {props.event.secondaryEndDateTime ? (
            <SSText
              variant="caption"
              color={warningDarkColor}
              fontSize="0.8rem"
              fontWeight={500}
            >
              {confirmByLabel}{" "}
              {formatSqlDate(props.event.secondaryEndDateTime, "MMM dd, h:mm a")}
            </SSText>
          ) : null}
        </Stack>
      );
    }

    // Fully confirmed
    if (secondaryStatus === "confirmed") {
      return (
        <Stack spacing={0.5} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
          <SSButton
            variant="outlined"
            colorScheme={successMainLightColor}
            endIcon={<FiEdit />}
            sx={{
              color: successMainLightColor,
              fontWeight: 700,
              fontSize: "0.9rem",
              ...(mdUp ? { width: "fit-content" } : {}),
            }}
            onClick={handleOpenAttendanceModal}
            fullWidth={!mdUp}
          >
            {confirmedLabel}
          </SSButton>
        </Stack>
      );
    }

    // Missed secondary window
    if (secondaryStatus === "missed") {
      return (
        <Stack spacing={0.5} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
          <SSButton
            variant="outlined"
            colorScheme={errorMainLightColor}
            sx={{
              color: errorMainLightColor,
              fontWeight: 700,
              fontSize: "0.9rem",
              opacity: 0.7,
              ...(mdUp ? { width: "fit-content" } : {}),
            }}
            disabled
            fullWidth={!mdUp}
          >
            {confirmationMissedLabel}
          </SSButton>
          <SSText
            variant="caption"
            color={errorMainLightColor}
            fontSize="0.8rem"
            fontWeight={500}
          >
            {confirmationWindowClosedLabel}
          </SSText>
        </Stack>
      );
    }
  }

  // --- Existing Primary Flow (unchanged) ---
  if (props.event?.isOptInEnabled) {
    // covers both optedIn false and null
    if (!Boolean(props?.event?.optedIn) && isReservationEnabled) {
      if (isExchange) {
        return (
          <Stack spacing={0.5} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
            <SSButton
              onClick={() => openExchangeModal(props.event)}
              sx={{
                backgroundColor: attendButtonColor,
                color: attendButtonTextColor,
                ["&:hover"]: {
                  backgroundColor: lighten(attendButtonColor, 0.2),
                },
                ...(mdUp ? { width: "fit-content" } : {}),
              }}
              fullWidth={!mdUp}
            >
              {exchangeButtonLabel}
            </SSButton>
          </Stack>
        );
      }

      return (
        <Stack spacing={0.5} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
          <SSButton
            onClick={handleOpenAttendanceModal}
            sx={{
              backgroundColor: attendButtonColor,
              color: attendButtonTextColor,
              ["&:hover"]: {
                backgroundColor: lighten(attendButtonColor, 0.2),
              },
              ...(mdUp ? { width: "fit-content" } : {}),
            }}
            disabled={isAttendenceDisabled}
            fullWidth={!mdUp}
          >
            {attendButtonLabel}
          </SSButton>
        </Stack>
      );
    }
    if (props.event?.optedIn === true) {
      return (
        <Stack spacing={1} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
          <SSButton
            variant="outlined"
            colorScheme={successMainLightColor}
            endIcon={<FiEdit />}
            sx={{
              color: successMainLightColor,
              fontWeight: 700,
              fontSize: `0.9rem`,
              ...(mdUp ? { width: "fit-content" } : {}),
            }}
            onClick={handleOpenAttendanceModal}
            fullWidth={!mdUp}
          >
            {attendButtonAttendingLabel}
          </SSButton>
        </Stack>
      );
    }
    if (props.event?.optedIn === false) {
      return (
        <Stack spacing={0.5} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
          <SSButton
            variant="outlined"
            colorScheme={errorMainLightColor}
            endIcon={<FiEdit />}
            sx={{
              color: errorMainLightColor,
              fontWeight: 700,
              fontSize: `0.9rem`,
              ...(mdUp ? { width: "fit-content" } : {}),
            }}
            disabled={isAttendenceDisabled}
            onClick={handleOpenAttendanceModal}
            fullWidth={!mdUp}
          >
            {attendButtonNotAttendingLabel}
          </SSButton>
        </Stack>
      );
    }

    return (
      <Stack spacing={0.5} sx={{ alignItems: mdUp ? "flex-start" : "stretch" }}>
        <SSButton
          onClick={handleOpenAttendanceModal}
          sx={{
            backgroundColor: attendButtonColor,
            ["&:hover"]: {
              backgroundColor: lighten(attendButtonColor, 0.2),
            },
            ...(mdUp ? { width: "fit-content" } : {}),
          }}
          disabled={isAttendenceDisabled}
          fullWidth={!mdUp}
        >
          {attendButtonLabel}
        </SSButton>
      </Stack>
    );
  }

  return (
    <SSText
      variant="body"
      color={
        props?.isDarkBg ? theme.colors.common.white : theme.colors.gray[400]
      }
      fontSize={`0.9rem`}
      fontWeight={500}
      mb={1}
    >
      {props?.event?.ticketDeliveryStatus && !hideTicketDeliveryStatus ? (
        <>
          {deliveryStatusLabel ? (
            <>
              <span
                style={{
                  color: ticketNotDelivered
                    ? errorMainLightColor
                    : successMainLightColor,
                  fontWeight: 700,
                }}
              >
                {deliveryStatusLabel}
              </span>
              <br />
            </>
          ) : null}

          {deliveryStatusMessage ? <span>{deliveryStatusMessage}</span> : null}
        </>
      ) : props?.event?.isReservationCoverageExceeded &&
        reservationCoverageExceededMessage ? (
        <>{reservationCoverageExceededMessage}</>
      ) : (
        <>
          {checkInLabel}:{" "}
          {props?.event?.isOptInEnabled === false &&
          hasSecondaryConfirmation(props?.event)
            ? "Closed"
            : formatSqlDate(
                props?.event?.optInStartDateTime,
                "EEEE, MMM dd, h:mm a"
              )}
        </>
      )}
    </SSText>
  );
}
